import { useState } from 'react';
import { approvePeriod } from '../../api/inventory';
import { periodLabel } from './ClosePeriodSelector';

interface Props {
  regionId:        string;
  regionName:      string;
  period:          string; // 'YYYY-MM'
  outstandingCount: number;
  onClose:         () => void;
  onApproved:      () => void;
}

export default function ApproveCloseModal(props: Props) {
  const { regionId, regionName, period, outstandingCount, onClose, onApproved } = props;
  const [notes, setNotes]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  async function handleApprove() {
    setSaving(true);
    setError(null);
    try {
      await approvePeriod(regionId, period, notes.trim() || undefined);
      onApproved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve period.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-[#1e2521] rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100 mb-1">
          Approve {periodLabel(period)} close
        </h2>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-4">
          {regionName}
        </p>

        {outstandingCount > 0 && (
          <div className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
            <strong>{outstandingCount}</strong> warehouse{outstandingCount === 1 ? '' : 's'} ha{outstandingCount === 1 ? 's' : 've'} not submitted for this period. Approving now means their stock counts won&apos;t be included in the regional aggregate.
          </div>
        )}

        <label className="block">
          <span className="text-sm font-medium text-stone-700 dark:text-stone-300">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            placeholder="Any context worth recording on the approval."
            className="mt-1 block w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] px-3 py-2 text-sm text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-moss-500 resize-none"
          />
        </label>

        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#242d28] transition"
          >
            Cancel
          </button>
          <button
            onClick={handleApprove}
            disabled={saving}
            className="px-4 py-2 text-sm rounded-lg bg-moss-600 text-white hover:bg-moss-700 transition disabled:opacity-50"
          >
            {saving ? 'Approving…' : 'Approve close'}
          </button>
        </div>
      </div>
    </div>
  );
}
